import type { Api } from '@pflegenavi/shared-frontend/platform';
import {
  get,
  getPhoenixApiBaseUrl,
  getPhoenixApiTenantUrl,
  modify,
} from '@pflegenavi/shared-frontend/platform';
import type { Tenant } from '@pflegenavi/frontend/tenant';
import type { AuthenticationContext } from '@pflegenavi/frontend/authentication';
import type {
  CreateIdvSessionBody,
  FeatureFlag,
  IdvPsc,
  IdvSession,
  LegalEntity,
  PaymentProcessor,
} from '@pflegenavi/shared/api';

export interface TenantInformation {
  name: string;
  payment_processor: PaymentProcessor;
  payment_processor_mode: string;
  allow_deletion: boolean;
  receipt_images_required: boolean;
  split_expense_export_by_cash_list: boolean;
  cardholder_creation_flow: 'invite' | 'register';
  inserted_at: Date;
  updated_at: Date;
}

export interface NursingHomeLegalEntity {
  id: string;
  mangopay_sca_enrolled: boolean;
  legal_person_type: 'BUSINESS' | 'ORGANIZATION' | 'SOLETRADER' | 'PARTNERSHIP';
  mandatory_cash_link_for_receipts: boolean;
}

export interface NursingHome {
  id: string;
  name: string;
  feature_flags: string[];
  min_payment_amount: number;
  max_payment_amount: number;
  min_payment_amount_employee_initiated: number;
  max_payment_amount_employee_initiated: number;
  in_settlement_min_payment_amount: number;
  min_resident_thresholds_value: number;
  max_resident_thresholds_value: number;
  payment_disabled: boolean;
  legal_entity_id: string;
  pdf_logo_id: string;
  cost_center: string | null;
  legal_entity: NursingHomeLegalEntity | null;
}

// Phoenix show/update response (snake_case), consumed directly by the pages.
export interface NursingHomeDetail {
  id: string;
  name: string;
  notes: string | null;
  cost_center: string | null;
  legal_entity_id: string | null;
  pdf_logo_id: string | null;
  min_payment_amount: number | null;
  max_payment_amount: number | null;
  payments_disabled: boolean | null;
  payments_disabled_until: string | null;
  payments_disabled_reason: string | null;
  phone: string | null;
  company: string | null;
  fax: string | null;
  email: string | null;
  website: string | null;
  address: {
    street: string | null;
    number: string | null;
    zip: string | null;
    city: string | null;
    country: string | null;
  } | null;
}

// Update body (wrapped as { data: ... }); address/phone nest under base_data.
export interface UpdateNursingHomeBody {
  name?: string;
  notes?: string;
  payments_disabled?: boolean;
  payments_disabled_until?: Date | null;
  payments_disabled_reason?: string | null;
  min_payment_amount?: number;
  max_payment_amount?: number;
  base_data?: {
    phone?: string;
    address?: string;
    house_number?: string;
    zip_code?: string;
    city?: string;
  };
}

export interface INursingHomePhoenixApi extends Api {
  getTenantInformation(): Promise<{
    data: TenantInformation;
  }>;

  getNursingHome(dataIn: { params: { id: string } }): Promise<{
    data: NursingHomeDetail;
  }>;

  updateNursingHome(dataIn: {
    params: { id: string };
    body: { data: UpdateNursingHomeBody };
  }): Promise<{
    data: NursingHomeDetail;
  }>;

  getTenantFeatureFlags(): Promise<{
    data: {
      feature_flags: FeatureFlag[];
    };
  }>;

  getNursingHomes(): Promise<{
    meta: {
      total_count: number;
    };
    data: NursingHome[];
  }>;

  getLegalEntities(): Promise<{
    meta: {
      total_count: number;
    };
    data: LegalEntity[];
  }>;

  getLegalEntityIdvSession(dataIn: {
    params: { legalEntityId: string };
  }): Promise<{ data: IdvSession }>;

  createLegalEntityIdvSession(dataIn: {
    params: { legalEntityId: string };
    body: CreateIdvSessionBody;
  }): Promise<{ data: IdvSession }>;

  retryLegalEntityIdvPsc(dataIn: {
    params: { legalEntityId: string; pscId: string };
  }): Promise<{ data: IdvPsc }>;
}

export class NursingHomePhoenixApi implements INursingHomePhoenixApi {
  baseUrl: string;
  baseUrlWithoutTenant: string;

  getTenantInformation: INursingHomePhoenixApi['getTenantInformation'];
  getTenantFeatureFlags: INursingHomePhoenixApi['getTenantFeatureFlags'];
  getLegalEntities: INursingHomePhoenixApi['getLegalEntities'];
  getNursingHomes: INursingHomePhoenixApi['getNursingHomes'];
  getLegalEntityIdvSession: INursingHomePhoenixApi['getLegalEntityIdvSession'];
  createLegalEntityIdvSession: INursingHomePhoenixApi['createLegalEntityIdvSession'];
  retryLegalEntityIdvPsc: INursingHomePhoenixApi['retryLegalEntityIdvPsc'];
  getNursingHome: INursingHomePhoenixApi['getNursingHome'];
  updateNursingHome: INursingHomePhoenixApi['updateNursingHome'];

  constructor(
    tenantId: Tenant,
    public authContext: AuthenticationContext,
    apiUrl?: string
  ) {
    this.baseUrl = getPhoenixApiTenantUrl(tenantId, apiUrl);

    this.baseUrlWithoutTenant = getPhoenixApiBaseUrl(apiUrl);

    this.getNursingHomes = get({
      authContext,
      url: `${this.baseUrl}/nursing-home`,
    });

    this.getNursingHome = get<{ id: string }, { data: NursingHomeDetail }>({
      authContext,
      url: ({ id }) => `${this.baseUrl}/nursing-home/${id}`,
    });

    this.updateNursingHome = modify<
      { data: UpdateNursingHomeBody },
      { data: NursingHomeDetail },
      { data: NursingHomeDetail },
      { id: string }
    >({
      authContext,
      method: 'PATCH',
      url: ({ id }) => `${this.baseUrl}/nursing-home/${id}`,
    });

    this.getTenantInformation = get({
      authContext,
      url: `${this.baseUrlWithoutTenant}/api/tenant/${tenantId}`,
    });

    this.getTenantFeatureFlags = get({
      authContext,
      url: `${this.baseUrl}/feature-flags`,
    });

    this.getLegalEntities = get({
      authContext,
      url: `${this.baseUrl}/legal-entity`,
    });

    this.getLegalEntityIdvSession = get({
      authContext,
      url: ({ legalEntityId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}/mangopay-idv-session`,
    });

    this.createLegalEntityIdvSession = modify({
      authContext,
      url: ({ legalEntityId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}/mangopay-idv-session`,
      method: 'POST',
    });

    this.retryLegalEntityIdvPsc = modify({
      authContext,
      url: ({ legalEntityId, pscId }) =>
        `${this.baseUrl}/legal-entity/${legalEntityId}/mangopay-idv-session/pscs/${pscId}/retry`,
      method: 'PUT',
    });
  }
}
